import { type ForwardedRef, type ReactElement, type RefObject } from 'react';
import { useMemo } from 'react';
import { Item, useListState } from 'react-stately';
import { Virtualizer } from '@react-aria/virtualizer';
import { ListLayout, type ListLayoutOptions } from '@react-stately/layout';
import { type Node } from '@react-types/shared';
import cn from 'classnames';
import { genericForwardRef } from '@direct-frontend/stdlib';
import { type BaseRowData, type DataProvider } from '@adv-frontend/data-provider';
import { useAsyncFlow } from '@adv-frontend/modules-flow';
import styles from './InfiniteScroll.module.css';

const EMPTY_ROWS_IDS: string[] = [];

/** Элемент виртуальной коллекции списка — react-stately требует object-тип */
type VirtualRowItem = {
    /** Виртуальный id строки из dataProvider */
    id: string;
};

export interface InfiniteScrollProps<RowData extends BaseRowData = BaseRowData> {
    /**
     * dataProvider для элементов
     * @link https://nda.ya.ru/t/S1VBR-zr74tgTc
     */
    dataProvider: DataProvider<RowData>;

    /**
     * Функция для отрисовки элемента. Если элемент еще не загружен, то вместо этого отрисуется stub
     * @param row данные загруженной строки из dataProvider
     * @returns верстка элемента
     * @example row => <ListItem id={row.id}>{row.name}</ListItem>
     */
    children: (row: RowData) => ReactElement | null;

    /**
     * Оценка высоты строки
     * @example estimatedRowHeight={44}
     */
    estimatedRowHeight?: number;

    /**
     * Элемент, который нужно показать вместо элемента во время загрузки
     * @example stub={<div>Loading...</div>}
     */
    stub: ReactElement;

    /**
     * Обработчик события скролла списка
     * @example onScroll={handleScroll}
     */
    onScroll?: (event: React.UIEvent<HTMLUListElement>) => void;

    /**
     *  Стили контейнера
     */
    className?: string;
}

interface ItemRendererProps<RowData extends BaseRowData> extends Pick<InfiniteScrollProps<RowData>, 'dataProvider'> {
    renderer: InfiniteScrollProps<RowData>['children'];
    node: Node<VirtualRowItem>;
    stub: ReactElement;
}

const ItemRenderer = function ItemRenderer<RowData extends BaseRowData>({
    node,
    renderer,
    dataProvider,
    stub,
}: ItemRendererProps<RowData>) {
    /**
     * Подписка на поток строки по виртуальному id триггерит загрузку соответствующего чанка
     * по мере попадания строк в видимую область виртуализатора
     */
    const rowDataFlow = useMemo(() => dataProvider.rowData(String(node.key)), [dataProvider, node.key]);
    const rowData = useAsyncFlow(rowDataFlow);

    if (rowData.isError) {
        return null;
    }

    /** Построчно не суспендимся — на время загрузки строки рисуем стаб */
    if (!rowData.isReady && !rowData.isRefreshing) {
        return stub;
    }

    return renderer(rowData.current());
};

/**
 * Компонент для создания виртуальных списков, принимает flow-based dataProvider и функцию-рендерер элементов
 *
 * Можно использовать статический массив или получать элементы из endpoint-датапровайдера
 * @example
 * const dataProvider = useMemo(() => createStaticDataProvider(data), [data]);
 *
 * return <InfiniteScroll dataProvider={dataProvider}>{row => <ListItem>{row.name}</ListItem>}</InfiniteScroll>
 *
 * @example
 * const dataProvider = useMemo(() => getItemsDataProvider(queryParams), [queryParams]);
 *
 * return <InfiniteScroll dataProvider={dataProvider}>{row => <ListItem>{row.name}</ListItem>}</InfiniteScroll>
 */
export const InfiniteScroll = genericForwardRef(function InfiniteScroll<RowData extends BaseRowData = BaseRowData>(
    { children, dataProvider, onScroll, estimatedRowHeight = 44, stub, className }: InfiniteScrollProps<RowData>,
    ref: ForwardedRef<HTMLDivElement>
) {
    const virtualizer = useMemo<ListLayoutOptions<VirtualRowItem>>(() => {
        return {
            estimatedRowHeight,
        };
    }, [estimatedRowHeight]);

    const rowsIdsResult = useAsyncFlow(dataProvider.rowsIds);

    /** До готовности списка виртуальных строк рисуем пустой список, не суспендимся */
    const rowsIds = rowsIdsResult.isReady || rowsIdsResult.isRefreshing ? rowsIdsResult.current() : EMPTY_ROWS_IDS;

    const listState = useListState<VirtualRowItem>({
        children: rowsIds.map((id) => <Item key={id}>{id}</Item>),
    });

    const layout = useMemo(() => new ListLayout<VirtualRowItem>(virtualizer), [virtualizer]);

    layout.collection = listState.collection;

    return (
        <Virtualizer
            className={cn(styles.container, className)}
            onScroll={onScroll}
            ref={ref as RefObject<HTMLDivElement>}
            sizeToFit="height"
            scrollDirection="vertical"
            layout={layout}
            collection={listState.collection}
        >
            {(type, node) =>
                type === 'item' ? (
                    <ItemRenderer dataProvider={dataProvider} node={node} renderer={children} stub={stub} />
                ) : null
            }
        </Virtualizer>
    );
});
